import type { CSSProperties } from 'react';
import { __ } from '@wordpress/i18n';
import classnames from 'classnames';
import './DateRangePicker.css';
import './CalendarSkeleton.css';

interface Props {
	numberOfMonths?: number;
}

// Most months span five weeks, so five rows shifts the layout least once the
// real month replaces the skeleton.
const WEEKS = 5;
const DAYS_PER_WEEK = 7;

// Days dimmed at either end to suggest the month's edges. No month is known
// yet, so the offsets are arbitrary.
const FIRST_DAY_CELL = 2;
const LAST_DAY_CELL = 32;

// Day columns from one month's left edge to the next's, gap included.
const MONTH_SPAN = DAYS_PER_WEEK + 1;

// Rows from the top: the caption, the weekdays, then the weeks.
const CAPTION_ROW = 0;
const WEEKDAY_ROW = 1;
const FIRST_WEEK_ROW = 2;

/**
 * One box of the skeleton. x and y place it in day columns and rows, which
 * sets when the sweep reaches it.
 */
function Bone({ modifier, x, y }: { modifier: string; x: number; y: number }) {
	return (
		<span
			className={`gpb-calendar-skeleton__bone gpb-calendar-skeleton__bone--${modifier}`}
			style={{ '--gpb-sk-x': x, '--gpb-sk-y': y } as CSSProperties}
		/>
	);
}

// The arrows sit over the month's last two day columns, so the sweep reaches
// them along with the days below.
const PREVIOUS_ARROW_COLUMN = DAYS_PER_WEEK - 1.25;
const NEXT_ARROW_COLUMN = DAYS_PER_WEEK - 0.5;

function Nav({ left }: { left: number }) {
	return (
		<nav className="rdp-nav">
			<span className="rdp-button_previous">
				<Bone
					modifier="nav"
					x={left + PREVIOUS_ARROW_COLUMN}
					y={CAPTION_ROW}
				/>
			</span>
			<span className="rdp-button_next">
				<Bone
					modifier="nav"
					x={left + NEXT_ARROW_COLUMN}
					y={CAPTION_ROW}
				/>
			</span>
		</nav>
	);
}

/**
 * One month of the skeleton: its caption, weekdays and weeks.
 */
function MonthSkeleton({ index, hasNav }: { index: number; hasNav: boolean }) {
	const left = index * MONTH_SPAN;

	return (
		<div className="rdp-month">
			<div className="rdp-month_caption">
				{index === 0 ? (
					<>
						<Bone modifier="month" x={left + 1} y={CAPTION_ROW} />
						<Bone modifier="year" x={left + 3} y={CAPTION_ROW} />
					</>
				) : (
					<Bone modifier="caption" x={left + 1.5} y={CAPTION_ROW} />
				)}
			</div>
			{hasNav && <Nav left={left} />}
			<table className="rdp-month_grid">
				<thead>
					<tr className="rdp-weekdays">
						{[...Array(DAYS_PER_WEEK)].map((_day, dayIndex) => (
							<th
								key={dayIndex}
								className="rdp-weekday"
								scope="col"
							>
								<Bone
									modifier="weekday"
									x={left + dayIndex}
									y={WEEKDAY_ROW}
								/>
							</th>
						))}
					</tr>
				</thead>
				<tbody className="rdp-weeks">
					{[...Array(WEEKS)].map((_week, weekIndex) => (
						<tr key={weekIndex} className="rdp-week">
							{[...Array(DAYS_PER_WEEK)].map((_day, dayIndex) => {
								const cell =
									weekIndex * DAYS_PER_WEEK + dayIndex;
								const isOverflow =
									cell < FIRST_DAY_CELL ||
									cell > LAST_DAY_CELL;

								return (
									<td
										key={dayIndex}
										className={classnames('rdp-day', {
											'gpb-calendar-skeleton__day--overflow':
												isOverflow,
										})}
									>
										<Bone
											modifier="day"
											x={left + dayIndex}
											y={FIRST_WEEK_ROW + weekIndex}
										/>
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/**
 * Calendar skeleton shown in place of the calendar while it loads.
 *
 * This component mimics the structure of react-day-picker, for one or two
 * months, so it takes up the same space as the calendar that replaces it.
 */
export function CalendarSkeleton({ numberOfMonths = 1 }: Props) {
	const isTwoMonths = numberOfMonths > 1;

	return (
		<>
			<span className="gpb-calendar-skeleton__status" role="status">
				{__('Loading available dates…', 'gp-bookings')}
			</span>
			<div
				className={classnames('rdp-root gpb-calendar-skeleton', {
					'gpb-booking-time-picker__day-picker--two-months':
						isTwoMonths,
				})}
				data-mode="single"
				data-multiple-months={isTwoMonths ? 'true' : undefined}
				data-nav-layout={isTwoMonths ? 'after' : undefined}
				aria-hidden="true"
			>
				<div className="rdp-months">
					{!isTwoMonths && <Nav left={0} />}
					{[...Array(numberOfMonths)].map((_, monthIndex) => (
						<MonthSkeleton
							key={monthIndex}
							index={monthIndex}
							hasNav={
								isTwoMonths && monthIndex === numberOfMonths - 1
							}
						/>
					))}
				</div>
			</div>
		</>
	);
}
